"use client";

import { useState } from "react";
import {
  useScroll,
  useSpring,
  useTransform,
  useMotionValueEvent,
  type MotionValue,
} from "framer-motion";

export type HeaderMode = "atTop" | "scrolled" | "searchOpen" | "menuOpen";

const TOP_BAR_H = 20;
const STRIP_H = 40;
const NAV_TOP = 56;
const NAV_BOTTOM = 44;
const STRIP_FADE_END = 30;
const STRIP_COLLAPSE_END = 120;
const TOPBAR_FADE_END = 40;
const TOPBAR_COLLAPSE_END = 70;
const NAV_FINISH = 160;

export interface HeaderMotion {
  navHeight: MotionValue<number>;
  topBarHeight: MotionValue<number>;
  topBarOpacity: MotionValue<number>;
  stripHeight: MotionValue<number>;
  stripOpacity: MotionValue<number>;
  compact: boolean;
}

const SPRING = { stiffness: 140, damping: 26, mass: 0.55 };

export function useHeaderMotion(): HeaderMotion {
  const { scrollY } = useScroll();
  const smoothScrollY = useSpring(scrollY, SPRING);

  const navHeight = useTransform(smoothScrollY, [0, NAV_FINISH], [NAV_TOP, NAV_BOTTOM], {
    clamp: true,
  });

  const topBarHeight = useTransform(smoothScrollY, [0, TOPBAR_COLLAPSE_END], [TOP_BAR_H, 0], {
    clamp: true,
  });
  const topBarOpacity = useTransform(smoothScrollY, [0, TOPBAR_FADE_END], [1, 0], { clamp: true });

  const stripHeight = useTransform(
    smoothScrollY,
    [STRIP_FADE_END, STRIP_COLLAPSE_END],
    [STRIP_H, 0],
    { clamp: true }
  );
  const stripOpacity = useTransform(
    smoothScrollY,
    [STRIP_FADE_END, STRIP_COLLAPSE_END - 20],
    [1, 0],
    { clamp: true }
  );

  const [compact, setCompact] = useState(false);
  useMotionValueEvent(smoothScrollY, "change", (v) => {
    setCompact((prev) => {
      if (!prev && v > 90) return true;
      if (prev && v < 50) return false;
      return prev;
    });
  });

  return {
    navHeight,
    topBarHeight,
    topBarOpacity,
    stripHeight,
    stripOpacity,
    compact,
  };
}
